import type {ValuationData} from "@reva/shared/valuation-data";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {WebError} from "../web-error";

/**
 * API interface that loads a valuation from a ValueAG order, importing it on demand if no valuation
 * exists for that order yet (POST {@link RevaApi.PATH_VALUATION_IMPORT_FROM_VALUE_API}).
 * #19481: `purpose` gilt nur für einen Erstimport; ohne ihn legt der Server eine Bewertung an.
 */
export class PersistenceImportFromOrdersApiInterface extends SimpleApiInterface<ValuationData> {
    private readonly orderId: string;
    private readonly clientId: string;
    private readonly purpose: RevaValuationPurpose | undefined;

    constructor(orderId: string, clientId: string, purpose?: RevaValuationPurpose) {
        super();
        this.orderId = orderId;
        this.clientId = clientId;
        this.purpose = purpose;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATION_IMPORT_FROM_VALUE_API]);
    }

    protected buildPayload(): object {
        return {
            valueOrderGuid: this.orderId,
            valueClientGuid: this.clientId,
            ...(this.purpose ? {purpose: this.purpose} : {}),
        };
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }

    protected handleApiErrors(e: Error): ValuationData {
        if (e instanceof WebError && e.status === 404 && e.getErrorCode() === "R200") {
            throw new ErrorI18n("address.error.no_order_found");
        }
        return super.handleApiErrors(e);
    }
}
